// src/utils/simpleItemEnhancer.js
/**
 * Simple item enhancer that works with embedded product data
 * This avoids API calls and uses data already present in the order/sale response
 */

export class SimpleItemEnhancer {
  /**
   * Enhance items using embedded product data (no API calls needed)
   * @param {Array} items - Original sale items
   * @returns {Array} Enhanced items
   */
  static enhanceItemsWithEmbeddedData(items) {
    if (!Array.isArray(items)) return []

    return items.map((item, index) => {
      // Extract data from embedded product object or item itself
      const productData = item.product || {}
      
      const enhanced = {
        ...item,
        name: this.getItemName(item, productData, index),
        description: this.getItemDescription(item, productData),
        category: this.getItemCategory(item, productData),
        sku: this.getItemSKU(item, productData),
        productType: this.getProductType(item, productData)
      }

      return enhanced
    })
  }

  /**
   * Get item name from available data
   */
  static getItemName(item, productData, index) {
    const possibleNames = [
      productData.name,
      productData.productName,
      productData.title,
      item.name,
      item.productName,
      item.product_name,
      item.itemName,
      item.title,
      `Item #${index + 1}`
    ]

    const name = possibleNames.find(name => name && typeof name === 'string' && name.trim())
    return name || `Item #${index + 1}`
  }

  /**
   * Get item description
   */
  static getItemDescription(item, productData) {
    return productData.description || 
           item.description || 
           item.notes || 
           ''
  }

  /**
   * Get item category
   */
  static getItemCategory(item, productData) {
    return productData.category?.name ||
           productData.categoryName ||
           item.category?.name ||
           item.category ||
           item.categoryName ||
           ''
  }

  /**
   * Get item SKU
   */
  static getItemSKU(item, productData) {
    return productData.sku ||
           item.sku ||
           item.productSKU ||
           item.productCode ||
           ''
  }

  /**
   * Get product type
   */
  static getProductType(item, productData) {
    return productData.type ||
           item.type ||
           item.productType ||
           'product'
  }

  /**
   * Debug: Analyze item structure
   */
  static debugItemStructure(items) {
    if (!Array.isArray(items) || items.length === 0) {
      console.log('❌ No items to analyze')
      return
    }

    console.group('🔍 Item Structure Analysis')
    
    items.forEach((item, index) => {
      console.group(`📦 Item ${index + 1}:`)
      console.log('Raw item:', item)
      console.log('Has product object:', !!item.product)
      console.log('Product data:', item.product || 'None')
      
      const enhanced = this.enhanceItemsWithEmbeddedData([item])[0]
      console.log('Enhanced item:', enhanced)
      console.groupEnd()
    })
    
    console.groupEnd()
  }
}